import type { ZaehlerEintrag } from './typen';

export interface ZaehlerStammdaten {
  id: string;
  zaehler_nummer: string;
  typ: string;
  standort: string | null;
  // Freitext-Einheitenbezug aus den Stammdaten — nur für den Abgleich per
  // passtZurEinheit() gebraucht (Task 2, Etappe 1.5). Optional, weil ältere
  // Aufrufer (Tests, ad-hoc erzeugte Zähler ohne Stammsatz) das Feld nicht
  // mitführen.
  einheit_bezeichnung?: string | null;
}

/**
 * Findet Zähler für einen Raum basierend auf ihrem Standort.
 *
 * Der Standort ist Freitext ("Keller", "keller links", "Bad"). Deshalb
 * Teilstring ohne Groß-/Kleinschreibung statt Gleichheit — der Mitarbeiter
 * soll den Zähler im Raum finden, in dem er tatsächlich steht.
 *
 * Zähler ohne Standort (null) werden keinem Raum zugeordnet.
 *
 * Hinweis: Weil die Standort-Spalte Freitext ist, können Zufallstreffer
 * auftreten (z.B. Zähler mit Standort "Keller Bad-Steigleitung" wird bei
 * Raum "Bad" gefunden). Das ist ein bewusst in Kauf genommener Kompromiss:
 * lieber ein Zähler zu viel angeboten (der Mitarbeiter sieht ihn und übergeht
 * ihn) als einen nicht finden, der vor Ort hängt.
 *
 * @param zaehler - Liste aller Zähler-Stammdaten
 * @param raum - Name des Raums (z.B. "Keller", "Bad")
 * @returns Gefilterte Zähler, deren Standort den Raum (case-insensitive) enthält
 */
export function zaehlerFuerRaum(zaehler: ZaehlerStammdaten[], raum: string): ZaehlerStammdaten[] {
  const suche = raum.trim().toLowerCase();
  if (!suche) return [];
  return zaehler.filter(z => (z.standort ?? '').toLowerCase().includes(suche));
}

/**
 * Konvertiert Zähler-Stammdaten in einen Zähler-Eintrag für die Ablesung.
 *
 * Vergibt eine neue lokale Kennung (UUID), setzt Art und Nummer aus den
 * Stammdaten, lässt den Stand leer (wird beim Rundgang erfasst) und
 * initialisiert Foto und Ablesung mit null.
 *
 * @param z - Zähler-Stammdaten
 * @param raum - Name des Raums, in dem der Zähler erfasst wird (kann null sein)
 * @returns Neuer Zähler-Eintrag mit eindeutiger lokaler ID
 */
export function zuZaehlerEintrag(z: ZaehlerStammdaten, raum: string | null): ZaehlerEintrag {
  return {
    id: crypto.randomUUID(),
    zaehler_id: z.id,
    art: z.typ,
    nummer: z.zaehler_nummer,
    stand: '',
    foto: null,
    raum,
    ablesung_id: null,
  };
}

/**
 * Ermittelt Zähler, die noch nicht erfasst wurden.
 *
 * Vergleicht Zähler-Stammdaten mit bereits erfassten Einträgen. Ein Zähler
 * gilt als erfasst, wenn seine ID in erfasst.zaehler_id vorkommt (nicht null).
 * Ad-hoc erfasste Zähler ohne Stammsatz (zaehler_id === null) werden ignoriert.
 *
 * @param alle - Alle Zähler-Stammdaten
 * @param erfasst - Liste bereits erfasster Zähler-Einträge
 * @returns Zähler, die nicht in erfasst vorkommen
 */
export function offeneZaehler(alle: ZaehlerStammdaten[], erfasst: ZaehlerEintrag[]): ZaehlerStammdaten[] {
  const erfasstIds = new Set(erfasst.map(e => e.zaehler_id).filter((id): id is string => id !== null));
  return alle.filter(z => !erfasstIds.has(z.id));
}

/**
 * Findet einen bereits erfassten Eintrag zu einem Zähler-Stammsatz — BLOCKGENAU über die
 * Kombination aus Raumblock (`zaehlerIdsImBlock`, die `zaehler_ids` des jeweiligen
 * RaumBlock) und `zaehler_id`, NICHT nur über `zaehler_id` allein (Review-Fund).
 *
 * Der Fall: zaehlerFuerRaum() bietet einen Stammsatz über den RAUMNAMEN an, nicht über die
 * Raumblock-Kennung — zwei gleichnamige Raumblöcke (z. B. zwei „Sonstiger Raum" ohne
 * eigene Bezeichnung, s. baueRaumBloecke() in katalog.ts) bekommen deshalb denselben
 * Stammsatz zur Auswahl angeboten. Dedupliziert man nur über `zaehler_id` (wie vorher),
 * "findet" der zweite Block den vom ersten bereits angelegten Eintrag und ändert ihn bei
 * jeder Eingabe mit, statt eine eigene Ablesung anzulegen — beide Karten würden am Ende
 * denselben Zählerstand zeigen und teilen.
 *
 * `zaehlerIdsImBlock` ist `undefined`-tolerant (`?? []`): ein RaumBlock aus einem älteren,
 * lokal begonnenen Entwurf könnte das Feld selbst vermissen lassen (dieselbe Vorsicht wie
 * überall sonst im Modul, s. lib/uebergabe/lokal.ts).
 */
export function findeEintragImBlock(
  erfasst: ZaehlerEintrag[],
  zaehlerIdsImBlock: string[] | undefined,
  stammId: string,
): ZaehlerEintrag | undefined {
  const ids = zaehlerIdsImBlock ?? [];
  return erfasst.find(z => z.zaehler_id === stammId && ids.includes(z.id));
}
